/** Amenity key used in API and DB. */
export type AmenityKey =
  | 'covered_parking'
  | 'pets_allowed'
  | 'electricity_meter'
  | 'water_meter'
  | 'natural_gas'
  | 'landline'
  | 'balcony_terrace'
  | 'reception_lobby'
  | 'elevator'
  | 'elevator_services'
  | 'prayer_room'
  | 'reception_waiting_room'
  | 'first_aid_center'
  | 'gym_health_club'
  | 'disability_facilities'
  | 'pool'
  | 'jacuzzi'
  | 'sauna'
  | 'steam_room'
  | 'childcare_center'
  | 'children_play_area'
  | 'garden'
  | 'bbq_area'
  | 'cafeteria'
  | 'business_center'
  | 'meeting_room'
  | 'security'
  | 'laundry_room'
  | 'laundry_service'
  | 'maintenance_services'
  | 'cleaning_services'
  | 'technical_services'
  | 'broadband_internet';

export interface AmenityOption {
  key: AmenityKey;
  labelEn: string;
  labelAr: string;
  category: 'features' | 'building' | 'health_fitness' | 'family_entertainment' | 'business_security' | 'laundry_kitchen' | 'cleaning_maintenance' | 'technical';
}

/** All amenities: Arabic labels from user spec + English. Categories match المزايا والخدمات (Amenities & services). */
export const AMENITIES: AmenityOption[] = [
  { key: 'covered_parking', labelEn: 'Covered parking', labelAr: 'مواقف مغطاة للسيارات', category: 'features' },
  { key: 'pets_allowed', labelEn: 'Pets allowed', labelAr: 'مسموح بالحيوانات الاليفة', category: 'features' },
  { key: 'electricity_meter', labelEn: 'Electricity meter', labelAr: 'عداد كهرباء', category: 'features' },
  { key: 'water_meter', labelEn: 'Water meter', labelAr: 'عداد مياه', category: 'features' },
  { key: 'natural_gas', labelEn: 'Natural gas', labelAr: 'غاز طبيعي', category: 'features' },
  { key: 'landline', labelEn: 'Landline', labelAr: 'خط أرضي', category: 'features' },
  { key: 'balcony_terrace', labelEn: 'Balcony or terrace', labelAr: 'شرفة أو تراس', category: 'building' },
  { key: 'reception_lobby', labelEn: 'Reception lobby', labelAr: 'ردهة إستقبال', category: 'building' },
  { key: 'elevator', labelEn: 'Elevator(s)', labelAr: 'مصاعد', category: 'building' },
  { key: 'elevator_services', labelEn: 'Elevator services', labelAr: 'خدمات المصاعد', category: 'building' },
  { key: 'prayer_room', labelEn: 'Prayer room', labelAr: 'غرفة صلاة', category: 'building' },
  { key: 'reception_waiting_room', labelEn: 'Reception / waiting room', labelAr: 'غرفة استقبال /انتظار', category: 'building' },
  { key: 'first_aid_center', labelEn: 'First aid center', labelAr: 'مركز للإسعافات الأولية', category: 'health_fitness' },
  { key: 'gym_health_club', labelEn: 'Gym or health club', labelAr: 'صالة رياضية أو نادي صحي', category: 'health_fitness' },
  { key: 'disability_facilities', labelEn: 'Disability facilities', labelAr: 'تسهيلات لأصحاب الهمم', category: 'health_fitness' },
  { key: 'pool', labelEn: 'Pool', labelAr: 'مسبح', category: 'health_fitness' },
  { key: 'jacuzzi', labelEn: 'Jacuzzi', labelAr: 'جاكوزي', category: 'health_fitness' },
  { key: 'sauna', labelEn: 'Sauna', labelAr: 'ساونا', category: 'health_fitness' },
  { key: 'steam_room', labelEn: 'Steam room', labelAr: 'غرفة بخار', category: 'health_fitness' },
  { key: 'childcare_center', labelEn: 'Childcare center', labelAr: 'مركز رعاية أطفال', category: 'family_entertainment' },
  { key: 'children_play_area', labelEn: 'Children play area', labelAr: 'منطقة لعب للأطفال', category: 'family_entertainment' },
  { key: 'garden', labelEn: 'Garden', labelAr: 'حديقة', category: 'family_entertainment' },
  { key: 'bbq_area', labelEn: 'BBQ area', labelAr: 'مكان مخصص للشواء', category: 'family_entertainment' },
  { key: 'cafeteria', labelEn: 'Cafeteria', labelAr: 'كافيتيريا', category: 'family_entertainment' },
  { key: 'business_center', labelEn: 'Business center', labelAr: 'مركز أعمال', category: 'business_security' },
  { key: 'meeting_room', labelEn: 'Meeting room', labelAr: 'غرفة اجتماعات', category: 'business_security' },
  { key: 'security', labelEn: 'Security', labelAr: 'أمن و حماية', category: 'business_security' },
  { key: 'laundry_room', labelEn: 'Laundry room', labelAr: 'غرفة غسيل', category: 'laundry_kitchen' },
  { key: 'laundry_service', labelEn: 'Laundry service', labelAr: 'خدمة غسيل ملابس', category: 'laundry_kitchen' },
  { key: 'maintenance_services', labelEn: 'Maintenance services', labelAr: 'خدمات صيانة', category: 'cleaning_maintenance' },
  { key: 'cleaning_services', labelEn: 'Cleaning services', labelAr: 'خدمات تنظيف', category: 'cleaning_maintenance' },
  { key: 'technical_services', labelEn: 'Technical services', labelAr: 'الخدمات التقنية', category: 'technical' },
  { key: 'broadband_internet', labelEn: 'Broadband internet', labelAr: 'انترنت واسع النطاق', category: 'technical' },
];

const categoryLabelsEn: Record<AmenityOption['category'], string> = {
  features: 'Features',
  building: 'Building',
  health_fitness: 'Health & Fitness',
  family_entertainment: 'Family & Entertainment',
  business_security: 'Business & Security',
  laundry_kitchen: 'Laundry & Kitchen',
  cleaning_maintenance: 'Cleaning & Maintenance',
  technical: 'Technical',
};

const categoryLabelsAr: Record<AmenityOption['category'], string> = {
  features: 'مزايا',
  building: 'البناء',
  health_fitness: 'الصحة واللياقة',
  family_entertainment: 'الترفيه والأسرة',
  business_security: 'الأعمال والأمن والحماية',
  laundry_kitchen: 'الغسيل والمطبخ',
  cleaning_maintenance: 'التنظيف والصيانة',
  technical: 'الخدمات التقنية',
};

export function getCategoryLabel(category: AmenityOption['category'], lang: 'en' | 'ar'): string {
  return lang === 'ar' ? categoryLabelsAr[category] : categoryLabelsEn[category];
}

export function getAmenityLabel(key: string, lang: 'en' | 'ar'): string {
  const a = AMENITIES.find((x) => x.key === key);
  if (!a) return key;
  return lang === 'ar' ? a.labelAr : a.labelEn;
}
